import { warehouseClient, extractStatusCode, extractResponseMessage } from "@/infrastructure/http/warehouseClient";
import { PlanLimitError } from "@/infrastructure/errors/planLimitError";
import type { Isle, CreateIsleInput, UpdateIsleStatusInput, IsleErrorCode, ApiEnvelope } from "@/domain/types";

export function createIsleError(code: IsleErrorCode): Error {
  const err = new Error(code);
  err.name = "IsleError";
  return err;
}

export function isIsleError(err: unknown): err is Error & { message: IsleErrorCode } {
  return err instanceof Error && err.name === "IsleError";
}

function mapError(statusCode: number): IsleErrorCode {
  if (statusCode === 409) return "DUPLICATE_NAME";
  if (statusCode === 400) return "VALIDATION";
  if (statusCode === 404) return "NOT_FOUND";
  if (statusCode === 403) return "FORBIDDEN";
  if (statusCode === 401) return "UNAUTHORIZED";
  return "UNKNOWN";
}

export async function listIsles(warehouseId: string): Promise<Isle[]> {
  const response = await warehouseClient.get<ApiEnvelope<Isle[]>>(`/isle?warehouseId=${warehouseId}`);
  return response.data.data ?? [];
}

export async function createIsle(input: CreateIsleInput): Promise<void> {
  try {
    await warehouseClient.post("/isle", input);
  } catch (err) {
    const status = extractStatusCode(err);
    if (status === 403) {
      const msg = extractResponseMessage(err);
      if (msg) throw new PlanLimitError(msg);
    }
    throw createIsleError(mapError(status));
  }
}

export async function updateIsleStatus(input: UpdateIsleStatusInput): Promise<void> {
  try {
    await warehouseClient.patch("/isle/status", input);
  } catch (err) {
    throw createIsleError(mapError(extractStatusCode(err)));
  }
}
